import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/i18n';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppShell from './AppShell';

import {
  API,
  getIncidentTypes,
  getRuhCategories,
  DeviationCard,
  CreateDeviationModal,
  DeviationDetailModal,
} from './deviations';

function SimpleDeviationsPage() {
  const { user, organization, logout } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deviations, setDeviations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDeviation, setSelectedDeviation] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [stats, setStats] = useState(null);
  const [permissions, setPermissions] = useState(null);

  // Get translated types and categories
  const INCIDENT_TYPES = getIncidentTypes(t);
  const RUH_CATEGORIES = getRuhCategories(t);

  // Fetch permissions for AppShell
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const token = localStorage.getItem('systemklart_token');
        const response = await axios.get(`${API}/ops/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } });
        setPermissions(response.data);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    };
    fetchPermissions();
  }, []);

  // Check for 'open' query param to auto-open a deviation
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && deviations.length > 0) {
      const devToOpen = deviations.find(d => d.id === openId);
      if (devToOpen) {
        setSelectedDeviation(devToOpen);
        // Clear the query param after opening
        setSearchParams({});
      }
    }
  }, [searchParams, deviations]);

  useEffect(() => {
    fetchDeviations();
    fetchStats();
  }, [filterType]);

  const fetchDeviations = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      let url = `${API}/ops/deviations`;
      if (filterType !== 'all') {
        url += `?type=${filterType}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeviations(response.data.deviations || []);
    } catch (error) {
      console.error('Failed to fetch deviations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/deviations/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const closeDeviation = async (deviationId, force = false) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/deviations/${deviationId}/close`, null, {
        params: { force },
        headers: { Authorization: `Bearer ${token}` }
      });

      // Check if backend requires confirmation (RUH without actions)
      if (response.data.requires_confirmation) {
        // Show confirmation dialog
        if (window.confirm(response.data.message)) {
          // Retry with force=true
          await closeDeviation(deviationId, true);
        }
        return;
      }

      toast.success(t('cases.caseClosed'));
      fetchDeviations();
      fetchStats();
      emitProgressRefresh('deviation-closed');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('cases.couldNotClose');
      toast.error(errorMsg);
    }
  };

  const deleteDeviation = async (deviationId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/deviations/${deviationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('common.delete') + ' ✓');
      fetchDeviations();
      fetchStats();
    } catch (error) {
      toast.error(t('cases.couldNotDelete'));
    }
  };

  const escalateToDeviation = async (deviationId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/deviations/${deviationId}/escalate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RUH eskalert til avvik');
      fetchDeviations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('cases.errors.couldNotEscalate'));
    }
  };

  const createFollowUpTask = async (deviationId, deviation) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/deviations/${deviationId}/create-task`, {
        title: `Oppfølging: ${deviation.title || deviation.description?.substring(0, 50)}`,
        description: deviation.description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Oppfølgingsoppgave opprettet');
      fetchDeviations();
    } catch (error) {
      toast.error(t('cases.errors.couldNotCreateTask'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const lang = i18n.language?.substring(0, 2) || 'nb';
    const locale = lang === 'en' ? 'en-US' : 'nb-NO';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const lang = i18n.language?.substring(0, 2) || 'nb';
    const locale = lang === 'en' ? 'en-US' : 'nb-NO';
    return date.toLocaleString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'low': 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
      'medium': 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
      'high': 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
    };
    return colors[severity] || 'bg-slate-500/20 text-slate-400';
  };

  const getSeverityLabel = (severity) => {
    return t(`cases.severity.${severity}`);
  };

  const getStatusLabel = (status) => {
    const statusMap = { 'open': 'open', 'in_progress': 'inProgress', 'closed': 'closed' };
    return t(`cases.status.${statusMap[status] || status}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-rose-500/20 text-rose-400 border border-rose-500/30',
      'in_progress': 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
      'closed': 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    };
    return colors[status] || 'bg-slate-500/20 text-slate-400';
  };

  const getTypeConfig = (type) => {
    // Use translated labels
    const configs = {
      ruh: { label: t('cases.type.ruh'), color: 'orange' },
      deviation: { label: t('cases.type.deviation'), color: 'red' },
      improvement: { label: t('cases.type.improvement'), color: 'blue' }
    };
    return configs[type] || configs.deviation;
  };

  const getTypeColor = (type) => {
    const colors = {
      'ruh': 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
      'deviation': 'bg-rose-500/20 text-rose-400 border border-rose-500/30',
      'improvement': 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
    };
    return colors[type] || 'bg-slate-500/20 text-slate-400';
  };

  const openDeviations = deviations.filter(d => d.status !== 'closed');
  const closedDeviations = deviations.filter(d => d.status === 'closed');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <AppShell permissions={permissions}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-orange-400">⚠️</span>
                <span className="text-sm font-medium text-slate-300">{t('cases.type.ruh')}</span>
              </div>
              <div className="text-2xl font-bold text-orange-400">{stats.ruh?.open || 0}</div>
              <div className="text-xs text-slate-500">{t('common.openCount')} {t('common.of')} {stats.ruh?.total || 0} {t('common.total')}</div>
            </div>
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-rose-400">❌</span>
                <span className="text-sm font-medium text-slate-300">{t('cases.type.deviation')}</span>
              </div>
              <div className="text-2xl font-bold text-rose-400">{stats.deviation?.open || 0}</div>
              <div className="text-xs text-slate-500">{t('common.openCount')} {t('common.of')} {stats.deviation?.total || 0} {t('common.total')}</div>
            </div>
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-blue-400">💡</span>
                <span className="text-sm font-medium text-slate-300">{t('cases.type.improvement')}</span>
              </div>
              <div className="text-2xl font-bold text-blue-400">{stats.improvement?.open || 0}</div>
              <div className="text-xs text-slate-500">{t('common.openCount')} {t('common.of')} {stats.improvement?.total || 0} {t('common.total')}</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-white" data-testid="deviations-page-title">{t('cases.title')}</h1>
            {/* Filter tabs */}
            <div className="flex gap-1 bg-slate-800/50 rounded-lg p-1">
              {[
                { value: 'all', label: t('common.all') },
                { value: 'ruh', label: t('cases.type.ruh') },
                { value: 'deviation', label: t('cases.type.deviation') },
                { value: 'improvement', label: t('cases.type.improvement') }
              ].map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setFilterType(tab.value)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    filterType === tab.value
                      ? 'bg-cyan-500 text-slate-950 shadow-sm'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid={`filter-${tab.value}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/25"
            data-testid="create-deviation-btn"
          >
            + {t('cases.registerCase')}
          </button>
        </div>

        {/* Åpne saker */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-3">
            {t('cases.openCases')} ({openDeviations.length})
          </h2>
          {openDeviations.length === 0 ? (
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-8 text-center" data-testid="no-open-deviations">
              <p className="text-slate-300 mb-1">{t('dashboard.allUnderControl')}</p>
              <p className="text-sm text-slate-500">{t('dashboard.noAttentionNeeded')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {openDeviations.map(dev => (
                <DeviationCard
                  key={dev.id}
                  deviation={dev}
                  onClose={() => closeDeviation(dev.id)}
                  onDelete={() => deleteDeviation(dev.id)}
                  onEscalate={() => escalateToDeviation(dev.id)}
                  onCreateTask={() => createFollowUpTask(dev.id, dev)}
                  onClick={() => setSelectedDeviation(dev)}
                  formatDate={formatDate}
                  getSeverityColor={getSeverityColor}
                  getSeverityLabel={getSeverityLabel}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  getTypeColor={getTypeColor}
                  getTypeConfig={getTypeConfig}
                  t={t}
                />
              ))}
            </div>
          )}
        </section>

        {/* Lukkede saker */}
        {closedDeviations.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">
              {t('cases.closedCases')} ({closedDeviations.length})
            </h2>
            <div className="space-y-3">
              {closedDeviations.map(dev => (
                <DeviationCard
                  key={dev.id}
                  deviation={dev}
                  isClosed
                  onDelete={() => deleteDeviation(dev.id)}
                  formatDate={formatDate}
                  getSeverityColor={getSeverityColor}
                  getSeverityLabel={getSeverityLabel}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                  getTypeColor={getTypeColor}
                  getTypeConfig={getTypeConfig}
                  t={t}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {showCreate && (
        <CreateDeviationModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchDeviations(); fetchStats(); }}
          RUH_CATEGORIES={RUH_CATEGORIES}
          t={t}
        />
      )}

      {selectedDeviation && (
        <DeviationDetailModal
          deviation={selectedDeviation}
          onClose={() => setSelectedDeviation(null)}
          onUpdate={() => { fetchDeviations(); fetchStats(); }}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          getSeverityColor={getSeverityColor}
          getSeverityLabel={getSeverityLabel}
          getTypeConfig={getTypeConfig}
          RUH_CATEGORIES={RUH_CATEGORIES}
          t={t}
        />
      )}
    </AppShell>
  );
}

export default SimpleDeviationsPage;
